import type {
  PracticeTest as PracticeTestData,
  PracticeTestGroup,
  PracticeTestProblem,
} from '@mathflat/domain/PracticeTest'
import type { Revision } from '@mathflat/domain/SchoolSystem'
import { computed, makeAutoObservable, makeObservable, observable } from 'mobx'
import CheckableMap from 'src/models/CheckableMap'

import type { PracticeDepartment } from '@/types/makeWorksheet'

import { DTO } from '../DTO'

export class PracticeTest extends DTO {
  year: number = new Date().getFullYear()
  month: number = new Date().getMonth() + 1
  grade: 1 | 2 | 3 = 1
  id = ''
  groupListColl: GroupListColl

  constructor({ grade, year, month, groupList }: PracticeTestData) {
    super()

    this.year = year
    this.month = month
    this.grade = grade
    this.groupListColl = new GroupListColl(groupList)
    this.id = `${year}-${month}-${grade}`

    makeObservable(this, {
      year: observable,
      month: observable,
      groupListColl: observable,
      grade: observable,
      title: computed,
    })
  }

  get allIds() {
    return this.groupListColl.allIds
  }

  get allChecked() {
    return this.groupListColl.groupList
      .map(({ allChecked }) => allChecked)
      .every((checked) => checked)
  }

  get title() {
    return `${this.year}년 ${this.month}월`
  }

  get checkedProblemIdList() {
    return this.groupListColl.checkedIds
  }

  allCheckedByProblemNumber = (_index) => {
    return this.groupListColl.allCheckedByProblemNumber(_index)
  }

  toggleAllCheck = () => {
    this.groupListColl.toggleAllCheck()
  }

  allCheck = () => {
    this.groupListColl.allCheck()
  }

  clearAllCheck = () => {
    this.groupListColl.clearAllCheck()
  }
}

class GroupListColl {
  groupList: ProblemListColl[] = []

  constructor(groupList: PracticeTestGroup[]) {
    this.groupList = groupList.map((group) => new ProblemListColl(group))
    makeAutoObservable(this)
  }

  get allIds() {
    return this.groupList.map(({ allIds }) => allIds).flat()
  }

  get checkedIds() {
    return this.groupList.map(({ checkedIds }) => checkedIds).flat()
  }

  get allChecked() {
    return this.groupList.map(({ allChecked }) => allChecked).every((checked) => checked)
  }

  allCheckedByProblemNumber = (value: number) => {
    return this.groupList.every((group) => {
      const target = group.toArr.find(({ problemNumber }) => problemNumber === value)
      if (target) {
        return group.isChecked(target.id)
      }

      // 예를 들어 1번을 선택했을 때에, '공통' 문제풀에서는 존재하지만
      // '기하'같은 21번부터 존재하는 문제풀에선 존재할 수 없으므로 true를 리턴함.
      return true
    })
  }

  allCheck = () => {
    this.groupList.forEach(({ allCheck }) => allCheck())
  }

  setCheckByGroupProblemNumber = (value: number, checked: boolean) => {
    this.groupList.forEach((group) => {
      const target = group.toArr.find(({ problemNumber }) => problemNumber === value)

      if (target) {
        if (checked) {
          group.setCheck(target.id)
        } else {
          group.deleteCheck(target.id)
        }
      }
    })
  }

  clearAllCheck = () => {
    this.groupList.forEach(({ clearAllCheck }) => clearAllCheck())
  }

  toggleAllCheck = () => {
    this.groupList.forEach(({ toggleAllCheck }) => toggleAllCheck())
  }

  getGroupByType = (type: ProblemListColl['type']) => {
    return this.groupList.find((group) => group.type === type)
  }
}

class ProblemListColl {
  problemMap: CheckableMap<PracticeTestProblem['id'], PracticeTestProblem>
  revision: Revision
  type: PracticeDepartment

  constructor(data: PracticeTestGroup) {
    this.type = data.type
    this.problemMap = CheckableMap.fromArr(data.problemList, 'id')
    this.revision = data.revisions.length > 0 ? data.revisions[0] : 'CURRICULUM_15'

    makeAutoObservable(this)
  }

  get koSubject() {
    switch (this.type) {
      case 'PROBABILITY_AND_STATISTICS_15':
      case 'PROBABILITY_AND_STATISTICS_09':
        return '확률과 통계'
      case 'CALCULUS_15':
        return '미적분'
      case 'GEOMETRY_15':
        return '기하'
      case 'CALCULUS_09':
        return '미분과 적분'
      case 'DISCRETE_MATHEMATICS_09':
        return '이산수학'
      case 'SCIENCE':
        return '이과'
      case 'LIBERAL':
        return '문과'
      case 'COMMON':
      default:
        return '공통'
    }
  }

  get allIds() {
    return [...this.problemMap.keys()]
  }

  get toArr() {
    return [...this.problemMap.values()]
  }

  get checkSize() {
    return this.problemMap.checkSize
  }

  get toCheckedArr() {
    return this.problemMap.toCheckedValues
  }

  get checkedIds() {
    return this.toCheckedArr.map(({ id }) => id)
  }

  get allChecked() {
    return this.problemMap.isFullyChecked
  }

  get isChildSomeChecked() {
    return this.problemMap.checkSize > 0
  }

  toggleCheck(key) {
    return this.problemMap.toggleCheck(key)
  }

  setCheck(key) {
    return this.problemMap.setCheck(key)
  }

  deleteCheck(key) {
    return this.problemMap.deleteCheck(key)
  }

  isChecked(key) {
    return this.problemMap.isChecked(key)
  }

  toggleAllCheck = () => {
    return this.problemMap.toggleAllCheck()
  }

  clearAllCheck = () => {
    this.problemMap.clearAllCheck()
  }

  allCheck = () => {
    this.problemMap.allCheck()
  }
}
